import React, { useEffect } from "react";
import { useLocation } from "react-router-dom";
import LdsButton from "../../components/LdsButton";
import { SideBar } from "../../components/SideBar";
import { useSelector } from "react-redux";

import { ReactLoaderSpinner } from "../../components/ReactLoaderSpinner/index.js";
import LdsImage from "../../components/LdsImage";
import References from "../../components/References";
import donutChart from "../../assets/module-1/1-2-4-donut-chart.png";

// services
import { useBookmarkModuleMutation } from "../../store/services/moduleServerApi";

const Module_1_2_4 = () => {
  const { pathname: pathName } = useLocation();
  const { menuItems } = useSelector((state) => state?.moduleSlice);

  const [bookmarkModule, { isLoading: isMenuLoading }] =
    useBookmarkModuleMutation();

  /* Start useEffect */
  useEffect(() => {
    bookmarkModule({ pathName });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // * onPageLoad
  /* End useEffect */

  return (
    <div className="row">
      {isMenuLoading && <ReactLoaderSpinner />}
      {menuItems?.id && <SideBar menuItems={[menuItems]} />}
      <div className="col-sm-8">
        <main>
          <section>
            <h1 className="blue text-uppercase b mt-5">
              1.2.4 Amyvid Is Reproducible<sup>1,2</sup>
            </h1>
            <p className="mt-1 mb-0">Estimated time to complete: 1 minute</p>
          </section>
          <section className="row mt-5">
            <h2 className="h5 mb-2 b">
              High inter-reader reproducibility<sup>2</sup>
            </h2>
            <p>
              Amyvid has been shown to have high inter-reader reproducibility for estimating beta-amyloid neuritic plaque density.<sup>1,2</sup>
            </p>
            <div className="d-flex align-items-center">
              <LdsImage
                src={donutChart}
                alt="Image showing that 92% of interpretations agreed with the majority, with a 95% confidence interval of 89.8% to 94.1% (n=59). High inter-reader reproducibility is highlighted, with an overall Fleiss’ kappa statistic of 0.83 (95% CI: 0.78 to 0.88, n=151), exceeding the prespecified success criterion."
                style={{ maxWidth: "600px" }}
                className="mx-auto d-block mt-4 mb-4"
              />
            </div>
            <div >
              <p>High inter-reader reproducibility</p>
              <ul>
                <li>
                  92% of interpretations agreed with the majority (95% CI: 89.8% to 94.1%) (n=59)<sup>2</sup>
                </li>
                <li>
                  Overall Fleiss’ kappa statistic* of 0.83 (95% CI: 0.78 to 0.88) (n=151); the lower bound of the 95% CI exceeded the prespecified success criterion<sup>1</sup>
                </li>
              </ul>
            </div>
            <div className="row row-flex">
              <div className="col-sm-12 col-md-6">
                <div className="blue-square-border p-0">
                  <h2 className="p b p-3 blue-bg white text-center mb-0">Study 2: Assessed the sensitivity and specificity of a binary visual read methodology in patients who underwent autopsy (truth standard)<sup>1</sup></h2>
                </div>
                <div className="blue-square-border notop light-blue-bg p-3">
                  <p className="b fz-md">Design</p>
                  <ul className="fz-md ms-1 ps-2">
                    <li>5 readers (trained in person)</li>
                    <li>Binary visual read methodology</li>
                  </ul>
                </div>
                <div className="blue-square-border notop light-blue-bg p-3" style={{ minHeight: "215px" }}>
                  <p className="b fz-md">Population</p>
                  <p className="fz-md">
                    59 terminally ill patients<sup>†</sup> who received
                    premortem Amyvid scans and then underwent autopsy to
                    establish a truth standard
                  </p>
                </div>
              </div>
              <div className="col-sm-12 col-md-6">
                <div className="blue-square-border p-0">
                  <h2 className="p b p-3 blue-bg white text-center mb-0">Study 3: Assessed the inter- and intra-reader image interpretation reproducibility in subjects with and without autopsy (truth standard)<sup>1</sup></h2>
                </div>
                <div className="blue-square-border notop light-blue-bg p-3">
                  <p className="b fz-md">Design</p>
                  <ul className="fz-md ms-1 ps-2">
                    <li>5 readers (trained using electronic media)</li>
                    <li>Binary visual read methodology</li>
                  </ul>
                </div>
                <div
                  className="blue-square-border notop light-blue-bg p-3"
                  style={{ minHeight: "200px" }}
                >
                  <p className="b fz-md">Population</p>

                  <p className="fz-md">
                    92 subjects<sup>‡</sup> who did not undergo autopsy (truth
                    standard) in addition to 59 patients (same population from
                    Study 2) with autopsy data
                  </p>
                  <p className="fz-md">
                    33 randomly selected images were used to assess intra-reader
                    reproducibility
                  </p>
                </div>
              </div>
            </div>
          </section>
          <section className="button-control mb-0">
            <LdsButton
              classes="white-button mt-3 mb-5 prev"
              onClick={function navClick() {
                window.open("/module-1/module-1-2-3", "_self");
              }}
            >
              Previous Lesson
            </LdsButton>
            <LdsButton
              classes="blue-button mt-3 mb-5 align-self-right float-end next"
              onClick={function navClick() {
                window.open("/module-1/module-1-2-5", "_self");
              }}
            >
              Next Lesson
            </LdsButton>
          </section>
          <section className="row mt-0 mb-5">
            <p className="fz-sm">
              *Fleiss' kappa: the degree of agreement among multiple readers over that which would be expected by chance.<sup>1,3</sup>
            </p>
            <p className="fz-sm">
              <sup>†</sup>Twenty-nine (29) patients had an Alzheimer’s disease (AD) clinical diagnosis, 13 had another type of dementing disorder, 12 had no history of cognitive impairment, and 5 had MCI.<sup>1</sup>
            </p>
            <p className="fz-sm">
              <sup>‡</sup>Twenty (20) healthy volunteers, 52 patients with MCI, 20 patients with AD.<sup>1</sup>
            </p>
            <p className="fz-sm">
              CI = confidence interval; AD=Alzheimer’s disease; MCI=mild cognitive impairment.
            </p>
          </section>


          <References>
            <ol className="no-indent fz-sm">
              <li>Data on File. Lilly USA, LLC. DOF-AM-US-0005.</li>
              <li>Clark CM, Pontecorvo MJ, Beach TG, et al; for AV-45-A16 Study Group. Cerebral PET with florbetapir compared with neuropathology at autopsy for detection of neuritic amyloid-β plaques: a prospective cohort study. <em>Lancet Neurol.</em> 2012;11(8):669-678.</li>
            </ol>
          </References>
        </main>
      </div>
    </div>
  );
};

export default Module_1_2_4;
